"use client";
import {
  Box,
  Button,
  FormControlLabel,
  InputAdornment,
  Radio,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import EmailIcon from "@mui/icons-material/Email";
import DateRangeIcon from "@mui/icons-material/DateRange";
import FemaleIcon from "@mui/icons-material/Female";
import { ApiService } from "../../services/AppService";
import { ResponseObject } from "@/app/classes/main";
import { getToken } from "../../services/HolderService";
import { CustomerUpdateRequest } from "../../classes/main";
import toast from "react-hot-toast";
import { trackEvent } from "@/app/utils/branch";
import { usePathname } from "next/navigation";

const StyledTextField = styled(TextField)({
  "& .MuiInputAdornment-positionStart": {
    marginRight: "5px",
  },
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused fieldset": {
      borderColor: "#15741a",
    },
  },
  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
    display: "none",
  },
  "& input[type=number]": {
    MozAppearance: "textfield",
  },
});

interface SignUpProfileProps {
  handleLoginCloseAfterSignUp: any;
}

const SignUpProfile: React.FC<SignUpProfileProps> = ({
  handleLoginCloseAfterSignUp,
}) => {
  const [selectedValue, setSelectedValue] = React.useState("");
  const [responseObject, setResponseObject] = useState<
    ResponseObject | undefined
  >(undefined);
  const apiService = useMemo(() => new ApiService(), []);
  const [values, setValues] = useState({
    name: "",
    email: "",
    mobile: "",
    date: "",
    gender: selectedValue ?? "",
  });
  const pathName = usePathname();

  const getCustomerDetails = useCallback(async () => {
    const responseObject: ResponseObject | undefined =
      await apiService.get<ResponseObject>(
        apiService.customerDetails,
        getToken()!
      );
    setResponseObject(responseObject);
  }, [apiService]);

  useEffect(() => {
    getCustomerDetails();
  }, [getCustomerDetails]);

  useEffect(() => {
    // console.log("value changed");
    setValues((prevValues) => ({
      ...prevValues,
      gender: responseObject?.object?.gender ?? "",
      name: responseObject?.object?.name ?? "",
      email: responseObject?.object?.workEmail ?? "",
      mobile: responseObject?.object?.mobile ?? "",
      date: responseObject?.object?.dob ?? "",
    }));
    setSelectedValue(responseObject?.object?.gender ?? "");
  }, [responseObject]);

  useEffect(() => {
    setValues((prevValues) => ({
      ...prevValues,
      gender: selectedValue,
    }));
  }, [selectedValue]);

  const handleChange = (event: any) => {
    setSelectedValue(event.target.value);
  };

  const handleDetails = (e: any) => {
    const { name, value } = e.target;
    setValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = () => {
    if (pathName == "/") {
      trackEvent("COMPLETE_REGISTRATION_FROM_HOME", null);
    } else if (pathName == "/cart") {
      trackEvent("COMPLETE_REGISTRATION_FROM_CART", null);
    }
    // console.log("--submit", values);
    const customerUpdateRequest = new CustomerUpdateRequest();
    customerUpdateRequest.name = values.name;
    customerUpdateRequest.workEmail = values.email;
    customerUpdateRequest.dob = values.date;
    customerUpdateRequest.gender = values.gender;
    // console.log(customerUpdateRequest);
    apiService
      .post(
        apiService.updateCustomerDetails,
        customerUpdateRequest,
        getToken()!
      )
      .then((value: any) => {
        // alert(value?.message);
        toast.success(value?.message, {
          duration: 5000,
          style: {
            padding: "16px",
          },
        });
        getCustomerDetails();
      });
    handleLoginCloseAfterSignUp();
    window.location.reload();
  };

  return (
    <Box
      component="form"
      sx={{
        display: "grid",
        width: "100%",
        margin: "auto",
        padding: " 1rem",
      }}
      // noValidate={false}
    >
      <StyledTextField
        id="input-with-icon-adornment"
        label="Username"
        required
        name="name"
        type="text"
        value={values.name}
        onChange={handleDetails}
        sx={{ margin: "0.5rem" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <AccountCircleIcon style={{ color: "#15741a" }} />
            </InputAdornment>
          ),
        }}
      />
      <StyledTextField
        id="input-with-icon-adornment"
        label="Mobile Number"
        required
        name="mobile"
        type="number"
        value={values.mobile}
        onChange={handleDetails}
        sx={{ margin: "0.5rem" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LocalPhoneIcon style={{ color: "#15741a" }} />
            </InputAdornment>
          ),
        }}
      />
      <StyledTextField
        id="input-with-icon-adornment"
        label="Email Id (Optional)"
        name="email"
        type="email"
        value={values.email}
        onChange={handleDetails}
        sx={{ margin: "0.5rem" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <EmailIcon style={{ color: "#15741a" }} />
            </InputAdornment>
          ),
        }}
      />
      <StyledTextField
        id="input-with-icon-adornment"
        label="Date of Birth"
        required
        name="date"
        type="date"
        value={values.date}
        onChange={handleDetails}
        sx={{ margin: "0.5rem" }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <DateRangeIcon style={{ color: "#15741a" }} />
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ display: "flex", alignItems: "center", margin: "1rem 0px" }}>
        <FemaleIcon style={{ color: "#15741a", margin: "1rem" }} />
        <Box>
          <Typography variant="body1">Gender</Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControlLabel
              control={<Radio color="success" />}
              label={<span style={{ fontSize: "14px" }}>Female</span>}
              labelPlacement="end"
              value="female"
              name="gender"
              checked={selectedValue === "female"}
              onChange={handleChange}
              sx={{ fontSize: "14px" }}
            />
            <FormControlLabel
              control={<Radio color="success" />}
              label={<span style={{ fontSize: "14px" }}>Male</span>}
              labelPlacement="end"
              value="male"
              name="gender"
              checked={selectedValue === "male"}
              onChange={handleChange}
            />
            <FormControlLabel
              control={<Radio color="success" />}
              label={<span style={{ fontSize: "14px" }}>Others</span>}
              labelPlacement="end"
              value="others"
              name="gender"
              checked={selectedValue === "others"}
              onChange={handleChange}
            />
          </Box>
        </Box>
      </Box>
      <Button
        variant="contained"
        color="success"
        sx={{
          background: "#15741a",
          borderRadius: "50px",
          margin: "1rem 0px",
        }}
        onClick={handleSubmit}
      >
        Submit
      </Button>
    </Box>
  );
};

export default SignUpProfile;
